/**
 * HOW TO USED IT
 *
 * import cStyles from '@dkrm/general-libs/theme/styles';
 *
 * somewhere in the render component...
 * <Text style=[cStyles.fwb, cStyles.fs1]>Hello World!</Text>
 *
 *
 */

import PropTypes from 'prop-types';
import COLORS from './colors';
import merge from 'lodash/merge';
import { getAdjustedStyles } from './styles.helper';

/**
 * define proptypes for style passing */
export const stylePropTypes = PropTypes.oneOfType([PropTypes.array, PropTypes.number, PropTypes.shape()]);
const K_NONE_SIZE = 0;
const K_EXTRA_SMALL_SIZE = 2;
const K_SMALL_SIZE = 4;
const K_MEDIUM_SIZE = 8;
const K_LARGE_SIZE = 12;
const K_EXTRA_LARGE_SIZE = 16;
const K_EXTRA_EXTRA_LARGE_SIZE = 24;
const K_EXTRA_EXTRA_EXTRA_LARGE_SIZE = 36;
const K_BORDER_EXTRA_SMALL_SIZE = 0.2;
const K_BORDER_SMALL_SIZE = 0.5;
const K_BORDER_MEDIUM_SIZE = 1;
const K_BORDER_LARGE_SIZE = 1.5;
const K_BORDER_EXTRA_LARGE_SIZE = 1.8;
export const K_FONT_FAMILY_NATIVE = 'Circular';
export const K_FONT_FAMILY_WEB = 'Circular,-apple-system,BlinkMacSystemFont,Roboto,Helvetica Neue,sans-serif';
export const K_WEIGHT_BOLD = '700';
export const K_WEIGHT_MEDIUM = '500';
export const K_WEIGHT_NORMAL = '400';
export const K_WEIGHT_LIGHT = '300';
export const K_MOBILE_MAX_WIDTH = 743;
export const K_MOBILE_MAX_WIDTH_S = 480;
export const K_DESKTOP_MAX_WIDTH_S = 940;
export const K_DESKTOP_MAX_WIDTH = 1200;
let styles = {
  /**
   * align
   */
  flexMiddle: {
    alignItems: 'center',
    justifyContent: 'center'
  },
  flexLeftMiddle: {
    alignItems: 'flex-start',
    justifyContent: 'center'
  },
  flexRightMiddle: {
    alignItems: 'flex-end',
    justifyContent: 'center'
  },
  flexRightTop: {
    alignItems: 'flex-end',
    justifyContent: 'flex-start'
  },
  flexMiddleLeft: {
    alignItems: 'center',
    justifyContent: 'flex-start'
  },
  flexMiddleRight: {
    alignItems: 'center',
    justifyContent: 'flex-end'
  },
  /**
   * flex
   */
  flexDirCol: {
    flexDirection: 'column'
  },
  flexDirRow: {
    flexDirection: 'row'
  },
  flex1: {
    flex: 1
  },
  flex2: {
    flex: 2
  },
  flex3: {
    flex: 3
  },
  flex4: {
    flex: 4
  },
  flex5: {
    flex: 5
  },
  flexWrap: {
    flexWrap: 'wrap'
  },
  /**
   * Position
   */
  fixedTop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    width: '100%'
  },
  fixedBottom: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    width: '100%'
  },
  absoluteTop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    width: '100%'
  },
  absoluteBottom: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    width: '100%'
  },
  /**
   * background-color
   */
  bgp: {
    backgroundColor: COLORS.C_PRIMARY
  },
  bgd: {
    backgroundColor: COLORS.CANVAS
  },
  bgm: {
    backgroundColor: COLORS.C_MEDIUM_LIGHT
  },
  bgs: {
    backgroundColor: COLORS.SECONDARY
  },
  bgr: {
    backgroundColor: 'red'
  },
  bgb: {
    backgroundColor: 'black'
  },
  bgw: {
    backgroundColor: 'white'
  },
  bgt: {
    backgroundColor: 'transparent'
  },
  /**
   * panel - sync web version
   */
  panelDark: {
    backgroundColor: COLORS.C_PANEL_DARK,
    padding: 10
  },
  panelLight: {
    backgroundColor: COLORS.C_PANEL_LIGHT,
    padding: 10
  },
  /**
   * font weight
   */
  fwb: {
    fontWeight: K_WEIGHT_BOLD
  },
  fwm: {
    fontWeight: K_WEIGHT_MEDIUM
  },
  fwn: {
    fontWeight: K_WEIGHT_NORMAL
  },
  fwl: {
    fontWeight: K_WEIGHT_LIGHT
  },
  /**
   * font size
   */
  fs1: {
    fontSize: 50
  },
  fs2: {
    fontSize: 40
  },
  fs3: {
    fontSize: 30
  },
  fs4: {
    fontSize: 24
  },
  fs5: {
    fontSize: 16
  },
  fs5s: {
    fontSize: 14
  },
  fs6: {
    fontSize: 12
  },
  fs7: {
    fontSize: 10
  },
  h1: {
    fontSize_MOBILE: 32,
    // mobile
    fontSize: 36,
    // desktop
    fontWeight: K_WEIGHT_BOLD,
    letterSpacing: -0.2,
    lineHeight: 40,
    color: COLORS.C_DARK
  },
  h2: {
    fontSize_MOBILE: 24,
    // mobile
    fontSize: 30,
    // desktop
    fontWeight: K_WEIGHT_BOLD,
    letterSpacing: -0.2,
    lineHeight: 30,
    color: COLORS.C_DARK
  },
  h3: {
    fontSize: 19,
    fontWeight: K_WEIGHT_BOLD,
    letterSpacing: -0.2,
    lineHeight: 24,
    color: COLORS.C_DARK
  },
  h4: {
    fontSize: 17,
    fontWeight: K_WEIGHT_BOLD,
    letterSpacing: -0.2,
    lineHeight: 22,
    color: COLORS.C_DARK
  },
  contentxs: {
    fontSize: 11,
    fontWeight: K_WEIGHT_NORMAL,
    letterSpacing: 0.2,
    lineHeight: 14,
    color: COLORS.C_DARK
  },
  contents: {
    fontSize: 15,
    fontWeight: K_WEIGHT_LIGHT,
    letterSpacing: 0.3,
    lineHeight: 18,
    color: COLORS.C_DARK
  },
  contentm: {
    fontSize_MOBILE: 17,
    // mobile
    fontSize: 17,
    // desktop
    fontWeight: K_WEIGHT_LIGHT,
    letterSpacing: 0.3,
    lineHeight: 22,
    color: COLORS.C_DARK
  },
  contentl: {
    fontSize: 19,
    fontWeight: K_WEIGHT_LIGHT,
    letterSpacing: 0.2,
    lineHeight: 22,
    color: COLORS.C_DARK
  },
  captions: {
    fontSize: 9,
    lineHeight: 11,
    letterSpacing: 0.2,
    fontWeight: K_WEIGHT_BOLD,
    color: '#108aa8'
  },
  captionm: {
    fontSize: 13,
    lineHeight: 16,
    letterSpacing: 0.2,
    fontWeight: K_WEIGHT_BOLD,
    color: '#39576a'
  },
  subtitlexs: {
    fontSize: 13,
    fontWeight: K_WEIGHT_MEDIUM,
    letterSpacing: -0.2,
    color: COLORS.C_DARK
  },
  subtitles: {
    fontSize: 15,
    fontWeight: K_WEIGHT_MEDIUM,
    letterSpacing: -0.2,
    lineHeight: 18,
    color: COLORS.C_DARK
  },
  subtitlem: {
    fontSize: 17,
    // desktop
    fontWeight: K_WEIGHT_MEDIUM,
    letterSpacing: -0.2,
    lineHeight: 22,
    color: COLORS.C_DARK
  },
  subtitlel: {
    fontSize: 19,
    // desktop
    fontWeight: K_WEIGHT_MEDIUM,
    letterSpacing: -0.2,
    lineHeight: 22,
    color: COLORS.C_DARK
  },
  buttonm: {
    fontSize: 15,
    fontWeight: K_WEIGHT_BOLD,
    letterSpacing: 0.2,
    lineHeight: 18
  },
  buttonl: {
    fontSize: 17,
    fontWeight: K_WEIGHT_BOLD,
    letterSpacing: 0.8,
    lineHeight: 22
  },
  input: {
    fontSize: 14,
    textAlign: 'left',
    color: COLORS.SEMI_MUTED,
    fontWeight: K_WEIGHT_NORMAL
  },
  inputLabel: {
    fontSize: 18,
    fontWeight: K_WEIGHT_NORMAL,
    letterSpacing: -0.2,
    lineHeight: 22,
    color: '#002022'
  },
  inputPlaceholder: {
    fontSize: 14,
    textAlign: 'left',
    color: COLORS.C_MUTED,
    fontWeight: K_WEIGHT_NORMAL
  },
  inputHelper: {
    fontSize: 13,
    fontWeight: K_WEIGHT_LIGHT,
    letterSpacing: -0.2,
    color: COLORS.C_LIGHT_GREY
  },
  highlighted: {
    color: COLORS.C_PRIMARY,
    fontWeight: K_WEIGHT_BOLD
  },
  filterText: {
    fontSize_MOBILE: 14,
    // mobile
    fontSize: 15,
    letterSpacing: 0,
    fontWeight: K_WEIGHT_NORMAL
  },
  /**
   * font family
   */
  ff: {
    fontFamily: K_FONT_FAMILY_NATIVE
  },
  ffWeb: {
    fontFamily: K_FONT_FAMILY_WEB
  },
  /**
   * font decoration
   */
  textUnderline: {
    textDecorationLine: 'underline'
  },
  textItalic: {
    fontStyle: 'italic'
  },
  /**
   * @deprecated
   * text decoration
   */
  tdlu: {
    textDecorationLine: 'underline'
  },
  /**
   * text alignment
   */
  tal: {
    textAlign: 'left'
  },
  tac: {
    textAlign: 'center'
  },
  tar: {
    textAlign: 'right'
  },
  /**
   * font color - sync web version
   */
  textPrimary: {
    color: COLORS.C_PRIMARY
  },
  textSecondary: {
    color: COLORS.C_SECONDARY
  },
  textMuted: {
    color: COLORS.C_MUTED
  },
  textBeach: {
    color: COLORS.C_BEACH
  },
  textBabu: {
    color: COLORS.C_PRIMARY
  },
  textRausch: {
    color: COLORS.C_SECONDARY
  },
  textLima: {
    color: COLORS.C_LIMA
  },
  textUra: {
    color: COLORS.C_URA
  },
  textLightGrey: {
    color: COLORS.C_LIGHT_GREY
  },
  textDark: {
    color: COLORS.C_DARK
  },
  textWhite: {
    color: COLORS.WHITE
  },
  textBlack: {
    color: COLORS.BLACK
  },
  /**
   * @deprecated
   * font color
   */
  fcd: {
    color: COLORS.BRAND_TEXT
  },
  fcs: {
    color: COLORS.SECONDARY
  },
  fcsh: {
    color: COLORS.SECONDARY_HIGHLIGHT
  },
  fcsd: {
    color: COLORS.SECONDARY_DISABLED
  },
  fcm: {
    color: COLORS.MUTED
  },
  fcsm: {
    color: COLORS.SEMI_MUTED
  },
  fcw: {
    color: COLORS.WHITE
  },
  fcb: {
    color: COLORS.BLACK
  },
  container: {
    padding: 6
  },
  /**
   * border
   */
  bcd: {
    borderColor: COLORS.BRAND_TEXT
  },
  bcs: {
    borderColor: COLORS.SECONDARY
  },
  bcm: {
    borderColor: COLORS.C_MEDIUM_LIGHT
  },
  bcp: {
    borderColor: COLORS.C_PRIMARY
  },
  bcw: {
    borderColor: COLORS.WHITE
  },
  shadown: {
    shadowColor: '#000000',
    shadowOffset: {
      width: 0,
      height: 0
    },
    shadowRadius: 0,
    shadowOpacity: 0,
    elevation: 0
  },
  shadows: {
    shadowColor: '#000000',
    shadowOffset: {
      width: 0,
      height: 1
    },
    shadowRadius: 1,
    shadowOpacity: 0.0975,
    elevation: 0.5
  },
  shadowm: {
    shadowColor: '#000000',
    shadowOffset: {
      width: 0,
      height: 1
    },
    shadowRadius: 1,
    shadowOpacity: 0.2,
    elevation: 1
  },
  shadowl: {
    shadowColor: '#000000',
    shadowOffset: {
      width: 0.4,
      height: 1.6
    },
    shadowRadius: 1,
    shadowOpacity: 0.3,
    elevation: 2
  },
  shadowxl: {
    shadowColor: '#000000',
    shadowOffset: {
      width: 0.5,
      height: 2
    },
    shadowRadius: 15,
    shadowOpacity: 0.3,
    elevation: 4
  },
  /**
   * position
   */
  abs: {
    position: 'absolute'
  },
  rel: {
    position: 'relative'
  },
  fill: {
    left: 0,
    right: 0,
    top: 0,
    bottom: 0
  },
  left: {
    left: 0
  },
  right: {
    right: 0
  },
  top: {
    top: 0
  },
  bottom: {
    bottom: 0
  },
  /**
   * rotate
   */
  rot0: {
    transform: [{
      rotate: '0deg'
    }]
  },
  rot1: {
    transform: [{
      rotate: '-30deg'
    }]
  },
  rot2: {
    transform: [{
      rotate: '-60deg'
    }]
  },
  rot3: {
    transform: [{
      rotate: '-90deg'
    }]
  },
  /*
   * Height
   */

  height100: {
    height: '100%'
  },
  height75: {
    height: '75%'
  },
  height50: {
    height: '50%'
  },
  height25: {
    height: '25%'
  },
  /**
   * width
   */
  width100: {
    width: '100%'
  },
  width75: {
    width: '75%'
  },
  width50: {
    width: '50%'
  },
  width25: {
    width: '25%'
  },
  // to use pageMaxWidth, its parent should has style flexDirCol
  pageMaxWidth: {
    alignSelf: 'center',
    maxWidth: K_MOBILE_MAX_WIDTH,
    width: '100%'
  },
  pageMaxWidths: {
    alignSelf: 'center',
    maxWidth: K_MOBILE_MAX_WIDTH_S,
    width: '100%'
  },
  desktopPageMaxWidth: {
    alignSelf: 'center',
    maxWidth: K_DESKTOP_MAX_WIDTH,
    width: '100%'
  },
  desktopPageMaxWidths: {
    alignSelf: 'center',
    maxWidth: K_DESKTOP_MAX_WIDTH_S,
    width: '100%'
  },
  /**
   * border radius
   */
  brn: {
    borderRadius: 0
  },
  brxs: {
    borderRadius: 3
  },
  brs: {
    borderRadius: 4
  },
  brm: {
    borderRadius: 8
  },
  brl: {
    borderRadius: 12
  },
  /**
   * opacity
   */
  o03: {
    opacity: 0.3
  },
  /**
   * custom margin
   */
  mt2: {
    marginTop: 2
  }
};
styles = getAdjustedStyles(styles);

/*
>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
  SPACING UTILITIES
>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>>
*/
/**
 * Used to adjust the default spacing between components
 * Use with great care!
 *
 * <type><direction><size> -impt
 *
 * m : margin;
 * p : padding;
 *
 * a = all
 * t = top
 * r = right
 * b = bottom
 * l = left
 * h = horizontal
 * v = vertical
 *
 * n    = none                       : 0px;
 * xs   = extra small                : 2px;
 * s    = small                      : 4px;
 * m    = medium                     : 8px;
 * l    = large                      : 12px;
 * xl   = extra large                : 16px;
 * xxl  = extra extra large          : 24px;
 * xxxl = extra extra extra large    : 36px;

 * ptxs, pts, ptm, ptl, ptxl, plxs, pls, dst..
 * mtxs, mts, mtm, mtl, mtxl, mlxs, mls, mlm, dst..
 */
const generateStyle = [{
  prefixKey: 'p',
  // padding
  midKey: ['t', 'l', 'r', 'b', 'v', 'h', 'a'],
  postfixKey: ['n', 'xs', 's', 'm', 'l', 'xl', 'xxl', 'xxxl'],
  value: [K_NONE_SIZE, K_EXTRA_SMALL_SIZE, K_SMALL_SIZE, K_MEDIUM_SIZE, K_LARGE_SIZE, K_EXTRA_LARGE_SIZE, K_EXTRA_EXTRA_LARGE_SIZE, K_EXTRA_EXTRA_EXTRA_LARGE_SIZE],
  prefixStyle: 'padding',
  postfixStyle: ['Top', 'Left', 'Right', 'Bottom', ['Top', 'Bottom'], ['Left', 'Right'], ['Left', 'Top', 'Bottom', 'Right']]
}, {
  prefixKey: 'm',
  // margin
  midKey: ['t', 'l', 'r', 'b', 'v', 'h', 'a'],
  postfixKey: ['n', 'xs', 's', 'm', 'l', 'xl', 'xxl', 'xxxl'],
  value: [K_NONE_SIZE, K_EXTRA_SMALL_SIZE, K_SMALL_SIZE, K_MEDIUM_SIZE, K_LARGE_SIZE, K_EXTRA_LARGE_SIZE, K_EXTRA_EXTRA_LARGE_SIZE, K_EXTRA_EXTRA_EXTRA_LARGE_SIZE],
  prefixStyle: 'margin',
  postfixStyle: ['Top', 'Left', 'Right', 'Bottom', ['Top', 'Bottom'], ['Left', 'Right'], ['Left', 'Top', 'Bottom', 'Right']]
}, {
  prefixKey: 'bw',
  // border width
  midKey: ['t', 'l', 'r', 'b', 'v', 'h', 'a'],
  postfixKey: ['n', 'xs', 's', 'm', 'l', 'xl'],
  value: [K_NONE_SIZE, K_BORDER_EXTRA_SMALL_SIZE, K_BORDER_SMALL_SIZE, K_BORDER_MEDIUM_SIZE, K_BORDER_LARGE_SIZE, K_BORDER_EXTRA_LARGE_SIZE],
  prefixStyle: 'border',
  postfixStyle: ['TopWidth', 'LeftWidth', 'RightWidth', 'BottomWidth', ['TopWidth', 'BottomWidth'], ['LeftWidth', 'RightWidth'], ['LeftWidth', 'TopWidth', 'BottomWidth', 'RightWidth']]
}];
generateStyle.map(currentStyle => {
  currentStyle.midKey.map((currMidKey, idxMidKey) => {
    currentStyle.postfixKey.map((currPostfixKey, idxPostfixKey) => {
      const key = `${currentStyle.prefixKey}${currMidKey}${currPostfixKey}`;
      const value = currentStyle.value[idxPostfixKey];
      const currPostfixStyles = currentStyle.postfixStyle[idxMidKey];
      const postfixStyles = currPostfixStyles instanceof Array ? currPostfixStyles : [currPostfixStyles];
      postfixStyles.map(currPostfixStyle => {
        const styleKey = `${currentStyle.prefixStyle}${currPostfixStyle}`;
        styles = merge(styles, {
          [key]: {
            [styleKey]: value
          }
        });
      });
    });
  });
});
export default styles;